import { useEffect } from 'react'
import { useQuery } from 'react-query'
import type { Location, NavigateFunction } from 'react-router'

import type { CatalogFilterSeam } from 'features/catalog/hooks/use-catalog-filter-hash/use-catalog-filter-seam'

const FILTERS_PARAM = 'filters'

// ---------------------------------------------------------------------------
// Private sub-hook of use-catalog-filter-hash. Owns the state -> URL half:
// once `settled` (see the orchestrator's doc comment for what that means),
// saves the panel through the seam and writes its hash into `?filters=`, or removes the
// param when the panel has no filters. The save is content-addressed (md5
// of the payload), so re-saving an unchanged, just-hydrated panel is a
// no-op write — harmless, not a loop.
// ---------------------------------------------------------------------------

export function useCatalogFilterWriter(props: {
  settled: boolean
  projectId: string
  seam: CatalogFilterSeam
  location: Location
  navigate: NavigateFunction
  lastWrittenHashRef: { current: string | null }
}): void {
  const { settled, projectId, seam, location, navigate, lastWrittenHashRef } =
    props

  const hasFilters = seam.panel.filters.length > 0

  const { data: writtenHash } = useQuery(
    ['catalog-filter-hash-write', projectId, seam.mode, seam.writeKey],
    () => seam.save(projectId),
    {
      enabled: settled && !!projectId && hasFilters,
      refetchOnWindowFocus: false,
      // A slow/failed save shouldn't pile up react-query's default
      // exponential-backoff retries on top of an already-slow backend call.
      retry: false,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  )

  useEffect(() => {
    if (!settled) return

    const searchParams = new URLSearchParams(location.search)
    const currentHash = searchParams.get(FILTERS_PARAM)

    if (!hasFilters) {
      if (!currentHash) {
        // The URL has already caught up to "no filters" — safe to forget
        // the last-written hash now, so a later back/forward navigation to
        // that same hash is recognized as needing a fresh hydration instead
        // of being silently ignored.
        lastWrittenHashRef.current = null
        return
      }
      // Deliberately does NOT null `lastWrittenHashRef` here (unlike the
      // write branch below) — React Router defers the location commit, and
      // nulling the ref before that lands let a mid-transition render see
      // the OLD hash still on the URL paired with an already-cleared ref,
      // indistinguishable from a foreign navigation — wrongly re-triggering
      // a hydration reload that restored the just-removed filter. Leaving
      // the ref alone is safe: `needsFetch` short-circuits false whenever
      // the URL's hash is falsy, both before this delete lands (ref still
      // matches) and after (hash is falsy, so the ref no longer matters).
      searchParams.delete(FILTERS_PARAM)
      navigate(
        { pathname: location.pathname, search: searchParams.toString() },
        { replace: true },
      )
      return
    }

    if (!writtenHash || writtenHash === currentHash) return

    // Written eagerly, unlike above: `needsFetch` has no falsy short-circuit
    // once `hashFromUrl` becomes this hash, so the ref must recognize it as
    // "ours" before the URL catches up, or hydration would treat its own
    // in-flight write as foreign and reload the value being replaced.
    searchParams.set(FILTERS_PARAM, writtenHash)
    lastWrittenHashRef.current = writtenHash
    navigate(
      { pathname: location.pathname, search: searchParams.toString() },
      { replace: true },
    )
  }, [
    settled,
    hasFilters,
    writtenHash,
    location.pathname,
    location.search,
    navigate,
    lastWrittenHashRef,
  ])
}
